import { styled } from "@mui/material/styles";
import { Stack } from "@mui/material";
import { HubCardStack } from "../../components";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";

export const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.hubColors.white,
    border: "none",
    padding: "0px",
    maxWidth: "none",
  },
}));

const colors = ["mainColor", "mainColor2"];
export const StyledStack = styled(Stack, {
  shouldForwardProp: (prop) =>
    !["opacity", "isDragging", "isDisabled", "view", "suggested"].includes(
      prop,
    ),
})(({ opacity = 0.1, view = 0, isDragging, isDisabled, suggested, theme }) => ({
  margin: "0px 0px",
  borderRadius: "5px",
  width: "100%",
  height: "100%",
  backgroundColor: suggested
    ? "rgba(59, 130, 246, 0.4)"
    : `rgba(${theme.palette[colors[view]]}, ${Math.max(opacity, 0.1)})`,
  boxSizing: "border-box",
  transition:
    "transform 0.2s cubic-bezier(0.2, 0, 0, 1), background-color 0.3s ease",
  overflow: "hidden",
  justifyContent: "center",
  ...(suggested && {
    outline: `1px dashed ${theme.palette.hubColors.blueMain}`,
    outlineOffset: -1,
  }),
  ...(isDragging
    ? {
        outline: `1px solid ${theme.palette.hubColors.blueMain}`,
        outlineOffset: -1,
      }
    : !suggested && {
        outline: `0.5px solid ${theme.palette.hubColors.white}`,
        outlineOffset: -0.5,
        ...(!isDisabled && {
          ":hover": {
            border: `1px solid`,
            borderColor: theme.palette.hubColors.mainFocus,
          },
        }),
      }),
  ...(isDisabled && {
    cursor: "not-allowed",
    backgroundColor: `rgba(200, 200, 200, ${Math.max(opacity, 0.1)})`,
    backgroundImage:
      "repeating-linear-gradient(135deg, transparent, transparent 4px, rgba(0,0,0,0.1) 4px, rgba(0,0,0,0.1) 5px)",
  }),
}));

export const StyledGraphStack = styled(Stack)({
  flexDirection: "row",
  position: "relative",
  gap: "20px",
  width: "100%",
  height: "100%",
});

export const StyledGridLegend = styled(Stack)({
  height: "100%",
  width: "29px",
  flexDirection: "column-reverse",
  justifyContent: "space-between",
  paddingBottom: "25px",
});

export const StyledGridLines = styled(Stack)(({ theme }) => ({
  width: "calc(100% - 40px)",
  height: "calc(100% - 25px)",
  justifyContent: "space-between",
  position: "absolute",
  top: "0px",
  right: "0px",
  ".MuiDivider-root": {
    margin: "9.5px 0px",
    borderColor: theme.palette.hubColors.greyMid,
  },
  ".MuiDivider-root.goal-line": {
    borderColor: theme.palette.hubColors.mainFocus,
    borderWidth: "1.25px",
  },
  ".MuiDivider-root:last-of-type": {
    margin: "18px 0 0 0",
  },
}));

export const StyledHubCardStack = styled(HubCardStack)(({ theme }) => ({
  width: "100%",
  height: "100%",
  position: "relative",
  borderRadius: 0,
  overflow: "hidden",
  border: "none",
  borderRight: "1px solid",
  borderColor: theme.palette.hubColors.greyLight,
  ".HubDroppablesContainer-root": {
    width: "100%",
    height: "100%",
    padding: "0px",
    backgroundColor: "transparent",
    overflow: "hidden",
    display: "grid",
    gridTemplateColumns: "repeat(6, 1fr)",
    ".HubDroppable-root": {
      height: "100%",
      width: "100%",
      justifyContent: "end",
      ":hover": {
        backgroundColor: theme.palette.hubColors.greyLightTransparent,
      },
      ".HubDroppable-box": {
        transition: "height 0.3s ease",
      },
      ".HubDraggable-root": {
        height: "100%",
        pointerEvents: "auto",
      },
    },
  },
}));

export const StyledLabelStack = styled(Stack)({
  display: "grid",
  gridTemplateColumns: "repeat(6, 1fr)",
  alignItems: "center",
  justifyContent: "center",
  gap: "10px",
  span: {
    textWrap: "nowrap",
    textAlign: "center",
  },
});

export const StyledBarTooltip = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.hubColors.white,
  borderRadius: "8px",
  padding: "12px 14px",
  minWidth: 180,
  maxWidth: 240,
  boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
  border: "1px solid",
  borderColor: theme.palette.hubColors.greyLight,
  svg: {
    color: theme.palette.hubColors.greyDark,
  },
}));

export const StyledAltMarker = styled("div")(({ theme }) => ({
  position: "absolute",
  left: 0,
  right: 0,
  height: 0,
  pointerEvents: "none",
  borderTop: `1.5px dashed ${theme.palette.hubColors.greyDark}`,
  opacity: 0.75,
  transition: "bottom 0.3s ease",
  ".alt-label": {
    position: "absolute",
    right: 2,
    top: -14,
    fontSize: 8,
    fontWeight: 600,
    lineHeight: 1,
    padding: "2px 4px",
    borderRadius: 3,
    backgroundColor: theme.palette.hubColors.greyDark,
    color: theme.palette.hubColors.white,
    whiteSpace: "nowrap",
  },
}));

export const StyledDropDateChip = styled(Stack)(({ theme }) => ({
  position: "absolute",
  bottom: -18,
  left: "50%",
  transform: "translateX(-50%)",
  padding: "2px 8px",
  borderRadius: 4,
  backgroundColor: theme.palette.hubColors.blueMain,
  color: theme.palette.hubColors.fontWhite,
  fontSize: 9,
  fontWeight: 600,
  whiteSpace: "nowrap",
  pointerEvents: "none",
  zIndex: 30,
  boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
}));

export const StyledGoalStack = styled(Stack)({
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "15px",
  "& .MuiOutlinedInput-root": {
    padding: "5px 10px",
    borderRadius: 6,
    svg: {
      width: 12,
      height: 12,
    },
  },
});
